import { useNavigate } from 'react-router-dom'
import { ArrowRight, Play, Menu, X } from 'lucide-react'
import { motion, useInView } from 'framer-motion'
import { useRef, useEffect, useCallback, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/store/auth'
import { useIntercom, trackInitialVisit } from '@/lib/intercom'
import { ArchitectureShowcase } from '@/components/landing/ArchitectureShowcase'
import { AgentShowcase } from '@/components/landing/AgentShowcase'

const VIDEO_URL = 'https://nfjrfnodpk6bnkhl.public.blob.vercel-storage.com/New%20Guideflow%20%281%29.mp4'

const TOOLS_BEFORE = [
  { name: 'SEMrush', cost: 249 },
  { name: 'HubSpot', cost: 450 },
  { name: 'Hootsuite', cost: 99 },
  { name: 'Jasper AI', cost: 82 },
  { name: 'Mailchimp', cost: 175 },
  { name: 'BuzzSumo', cost: 159 },
  { name: 'Ahrefs', cost: 199 },
  { name: 'Buffer', cost: 36 },
  { name: 'Mention', cost: 79 },
  { name: 'Google Analytics 360', cost: 150 },
] as const

const TOTAL_BEFORE = TOOLS_BEFORE.reduce((sum, t) => sum + t.cost, 0)
const SYNAP_PRICE = 299
const ANNUAL_SAVINGS = (TOTAL_BEFORE - SYNAP_PRICE) * 12

const COMPARISON_ROWS = [
  { feature: 'Multi-Agent AI Orchestration', synap: true, hubspot: false, salesforce: false, jasper: false, klaviyo: false },
  { feature: '16 Specialized Marketing Agents', synap: true, hubspot: false, salesforce: false, jasper: false, klaviyo: false },
  { feature: 'Trust-Tiered Autonomy', synap: true, hubspot: false, salesforce: false, jasper: false, klaviyo: false },
  { feature: 'Brand Voice AI (Patent-Pending)', synap: true, hubspot: false, salesforce: false, jasper: 'partial' as const, klaviyo: false },
  { feature: 'SEO & Content Tools', synap: true, hubspot: true, salesforce: false, jasper: true, klaviyo: false },
  { feature: 'Email Marketing', synap: true, hubspot: true, salesforce: true, jasper: false, klaviyo: true },
  { feature: 'Social Media Management', synap: true, hubspot: true, salesforce: false, jasper: false, klaviyo: false },
  { feature: 'Analytics & Attribution', synap: true, hubspot: true, salesforce: true, jasper: false, klaviyo: true },
  { feature: 'Human-in-the-Loop Control', synap: true, hubspot: 'partial' as const, salesforce: 'partial' as const, jasper: false, klaviyo: false },
  { feature: 'Real-Time Agent Monitoring', synap: true, hubspot: false, salesforce: false, jasper: false, klaviyo: false },
] as const

const FEATURES = [
  {
    number: '01',
    title: 'Define your brand once',
    description: 'Upload your voice, values, and guidelines. Every agent aligns to your identity across all channels automatically. Multi-brand support for agencies.',
  },
  {
    number: '02',
    title: 'Agents execute autonomously',
    description: '16 specialized agents handle SEO, content, social, email, PR, ads, and more. Watch them work in real-time. Override anything, anytime.',
  },
  {
    number: '03',
    title: 'You stay in control',
    description: 'Five trust tiers from full autopilot to human-required. Budget changes need approval. SEO audits run automatically. You set the rules.',
  },
] as const

function FadeIn({ children, className, delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

function ComparisonCell({ value }: { value: boolean | 'partial' }) {
  if (value === true) return <span className="text-primary-400">&#10003;</span>
  if (value === 'partial') return <span className="text-white/40">~</span>
  return <span className="text-white/20">&mdash;</span>
}

export function LandingPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const { trackCTA } = useIntercom({
    pageName: 'Landing Page',
    pageCategory: 'landing',
  })

  useEffect(() => {
    trackInitialVisit()
  }, [])

  useEffect(() => {
    if (user) {
      navigate('/dashboard')
    }
  }, [user, navigate])

  // All CTAs book a call now — one funnel via Calendly, no self-serve signup
  // (per Steve, 2026-07-31).
  const handleStartTrial = useCallback((location: string) => {
    trackCTA('Book a Call', location, 'https://calendly.com/gtmvp')
    window.open('https://calendly.com/gtmvp', '_blank', 'noopener,noreferrer')
  }, [trackCTA])

  const handleViewPricing = useCallback((location: string) => {
    trackCTA('View Pricing', location, '/pricing')
    navigate('/pricing')
  }, [navigate, trackCTA])

  const handleSignIn = useCallback(() => {
    trackCTA('Sign In', 'header', '/auth/login')
    navigate('/auth/login')
  }, [navigate, trackCTA])

  return (
    <div className="min-h-screen" style={{ background: '#080F14' }}>

      {/* ── Navigation ── */}
      <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-[#080F14]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="Synap" className="h-8 w-8" />
            <span className="text-lg font-bold text-white tracking-tight">Synap</span>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-white/50 hover:text-white transition-colors">Features</a>
            <a href="#agents" className="text-sm text-white/50 hover:text-white transition-colors">Agents</a>
            <a href="/pricing" className="text-sm text-white/50 hover:text-white transition-colors">Pricing</a>
          </nav>

          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={handleSignIn}
              className="text-sm text-white/50 hover:text-white transition-colors"
            >
              Sign In
            </button>
            <Button size="sm" onClick={() => handleStartTrial('header')}>
              Start Trial
            </Button>
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden p-2 text-white/60 hover:text-white transition-colors"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/[0.06] px-6 py-4 space-y-4">
            <a href="#features" className="block text-sm text-white/50 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#agents" className="block text-sm text-white/50 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>Agents</a>
            <a href="/pricing" className="block text-sm text-white/50 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <div className="pt-2 border-t border-white/[0.06] flex flex-col gap-3">
              <button
                onClick={() => { setMobileMenuOpen(false); handleSignIn() }}
                className="text-sm text-white/50 hover:text-white transition-colors text-left"
              >
                Sign In
              </button>
              <Button size="sm" className="w-full" onClick={() => { setMobileMenuOpen(false); handleStartTrial('header-mobile') }}>
                Start Trial
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section className="min-h-[calc(100vh-4rem)] flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 sm:py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="max-w-3xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0 }}
              className="text-xs uppercase tracking-[0.2em] text-primary-400 font-medium mb-6"
            >
              Marketing OS
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.05] tracking-[-0.03em] mb-6"
            >
              Your marketing{'\n'}department,{'\n'}
              <span className="block">running on autopilot.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-xl text-white/60 leading-relaxed mb-10 max-w-lg"
            >
              16 autonomous AI agents replace your entire marketing tool stack. Full transparency. Complete control.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 mb-5"
            >
              <Button
                size="lg"
                onClick={() => handleStartTrial('hero')}
                icon={<ArrowRight className="h-4 w-4" />}
                iconPosition="right"
              >
                Book a Call
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => {
                  document.getElementById('platform-demo')?.scrollIntoView({ behavior: 'smooth' })
                }}
                icon={<Play className="h-4 w-4" />}
              >
                Watch Demo
              </Button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-sm text-white/40"
            >
              Free strategy call &middot; No commitment
            </motion.p>
          </div>

          {/* Video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative"
            id="platform-demo"
          >
            <div className="absolute inset-0 -m-16 rounded-full bg-primary-400/10 blur-3xl pointer-events-none" />
            <div className="relative rounded-xl border border-white/[0.08] overflow-hidden">
              <video
                className="w-full block"
                controls
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={VIDEO_URL} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Metrics Strip ── */}
      <FadeIn>
        <section className="border-y border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-12 text-center">
            {[
              { value: '16', label: 'Agents' },
              { value: '9', label: 'Channels' },
              { value: '5', label: 'Trust Tiers' },
              { value: '$299', label: 'per month' },
            ].map((metric) => (
              <div key={metric.label}>
                <p className="text-4xl font-bold text-white tracking-tight">{metric.value}</p>
                <p className="text-sm text-white/40 mt-1">{metric.label}</p>
              </div>
            ))}
          </div>
        </section>
      </FadeIn>

      {/* ── Architecture Showcase ── */}
      <ArchitectureShowcase />

      {/* ── Replace Your Stack ── */}
      <section className="py-12 sm:py-16 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
                Replace your entire tool stack
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto">
                Stop paying for 10+ subscriptions. Get everything in one platform.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 lg:gap-12 items-start">
              {/* Before */}
              <div>
                <p className="text-sm text-white/40 uppercase tracking-wider mb-6">Before</p>
                <div className="space-y-2">
                  {TOOLS_BEFORE.map((tool) => (
                    <div
                      key={tool.name}
                      className="flex items-center justify-between py-2.5 px-4 border-b border-white/[0.06]"
                    >
                      <span className="text-sm text-white/40">{tool.name}</span>
                      <span className="text-sm text-white/40 tabular-nums">${tool.cost}/mo</span>
                    </div>
                  ))}
                  <div className="pt-4 flex items-center justify-between">
                    <span className="text-sm text-white/60">Total</span>
                    <span className="text-2xl font-bold text-white">${TOTAL_BEFORE.toLocaleString()}/mo</span>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:flex flex-col items-center justify-center self-stretch">
                <div className="w-px flex-1 bg-white/[0.06]" />
                <span className="text-white/20 text-2xl my-4">&rarr;</span>
                <div className="w-px flex-1 bg-white/[0.06]" />
              </div>

              {/* After */}
              <div>
                <p className="text-sm text-white/40 uppercase tracking-wider mb-6">After</p>
                <div className="rounded-xl border border-white/[0.08] p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <img src="/logo192.png" alt="Synap" className="w-12 h-12" />
                    <div>
                      <p className="text-lg font-semibold text-white">Synap Marketing OS</p>
                      <p className="text-sm text-white/40">16 agents, all channels</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-8 text-sm text-white/60">
                    {['SEO', 'Content', 'Social', 'Email', 'Analytics', 'PR', 'Ads', 'Video'].map((f) => (
                      <span key={f} className="flex items-center gap-2">
                        <span className="text-primary-400 text-xs">&#10003;</span>
                        {f}
                      </span>
                    ))}
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-white/40 mb-1">Starting at</p>
                    <p className="text-4xl font-bold text-white">${SYNAP_PRICE}/mo</p>
                  </div>
                </div>

                <p className="text-3xl font-bold text-primary-400 text-center mt-8">
                  Save ${ANNUAL_SAVINGS.toLocaleString()}/year
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="features" className="py-12 sm:py-16 lg:py-28 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-20">
              How it works
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 lg:gap-16">
            {FEATURES.map((feature, i) => (
              <FadeIn key={feature.number} delay={i * 0.1}>
                <div className="relative">
                  <span className="text-[5rem] font-bold text-white/[0.04] leading-none absolute -top-6 -left-2 select-none pointer-events-none">
                    {feature.number}
                  </span>
                  <div className="relative">
                    <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                    <p className="text-base text-white/60 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison Table ── */}
      <section className="py-12 sm:py-16 lg:py-28 border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
                The only true Marketing OS
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto">
                Other platforms do pieces. Synap does everything with 16 specialized AI agents.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    <th className="py-2 sm:py-4 px-2 sm:px-4 text-left text-white/40 font-normal text-xs sm:text-sm">Feature</th>
                    <th className="py-2 sm:py-4 px-2 sm:px-4 text-center bg-white/[0.02] text-xs sm:text-sm">
                      <span className="text-primary-400 font-semibold">Synap</span>
                    </th>
                    <th className="py-2 sm:py-4 px-2 sm:px-4 text-center text-white/40 font-normal text-xs sm:text-sm">HubSpot</th>
                    <th className="py-2 sm:py-4 px-2 sm:px-4 text-center text-white/40 font-normal text-xs sm:text-sm">Salesforce</th>
                    <th className="py-2 sm:py-4 px-2 sm:px-4 text-center text-white/40 font-normal text-xs sm:text-sm">Jasper</th>
                    <th className="py-2 sm:py-4 px-2 sm:px-4 text-center text-white/40 font-normal text-xs sm:text-sm">Klaviyo</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row) => (
                    <tr key={row.feature} className="border-b border-white/[0.04]">
                      <td className="py-2 sm:py-3.5 px-2 sm:px-4 text-white/60 text-xs sm:text-sm">{row.feature}</td>
                      <td className="py-2 sm:py-3.5 px-2 sm:px-4 text-center bg-white/[0.02] text-xs sm:text-sm">
                        <ComparisonCell value={row.synap} />
                      </td>
                      <td className="py-2 sm:py-3.5 px-2 sm:px-4 text-center text-xs sm:text-sm"><ComparisonCell value={row.hubspot} /></td>
                      <td className="py-2 sm:py-3.5 px-2 sm:px-4 text-center text-xs sm:text-sm"><ComparisonCell value={row.salesforce} /></td>
                      <td className="py-2 sm:py-3.5 px-2 sm:px-4 text-center text-xs sm:text-sm"><ComparisonCell value={row.jasper} /></td>
                      <td className="py-2 sm:py-3.5 px-2 sm:px-4 text-center text-xs sm:text-sm"><ComparisonCell value={row.klaviyo} /></td>
                    </tr>
                  ))}
                  <tr className="border-t border-white/[0.08]">
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-white font-medium text-xs sm:text-sm">Starting Price</td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-center bg-white/[0.02] text-xs sm:text-sm">
                      <span className="text-primary-400 font-bold">$299/mo</span>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-center text-white/40 text-xs sm:text-sm">$800+/mo</td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-center text-white/40 text-xs sm:text-sm">$1,250+/mo</td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-center text-white/40 text-xs sm:text-sm">$82/mo</td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-center text-white/40 text-xs sm:text-sm">$175+/mo</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4 text-xs text-white/30">
              <span className="text-white/40">~</span> = Partial / limited functionality &nbsp;&nbsp;
              <span className="text-white/20">&mdash;</span> = Not available
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Agent Showcase ── */}
      <section id="agents" className="py-12 sm:py-16 lg:py-28 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AgentShowcase onTryDashboard={() => handleStartTrial('agent-showcase')} />
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-12 sm:py-16 lg:py-28 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
              Ready to automate<br />your marketing?
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-xl mx-auto">
              Replace 10+ tools. Save $1,500/month. Get 20 hours back every week.
            </p>
            <Button
              size="lg"
              onClick={() => handleStartTrial('footer-cta')}
              icon={<ArrowRight className="h-4 w-4" />}
              iconPosition="right"
            >
              Book a Call
            </Button>
            <p className="text-sm text-white/40 mt-5">
              Free strategy call &middot; No commitment
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/[0.06] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo192.png" alt="Synap" className="h-7 w-7" />
                <span className="text-base font-bold text-white">Synap</span>
              </div>
              <p className="text-sm text-white/40 leading-relaxed">
                The autonomous marketing OS.<br />
                16 agents. One platform. Zero busywork.
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-white mb-4">Product</p>
              <ul className="space-y-2.5 text-sm text-white/40">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#agents" className="hover:text-white transition-colors">AI Agents</a></li>
              </ul>
            </div>

            <div>
              <p className="text-sm font-medium text-white mb-4">Company</p>
              <ul className="space-y-2.5 text-sm text-white/40">
                <li><a href="/status" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>

            <div>
              <p className="text-sm font-medium text-white mb-4">Contact</p>
              <a href="mailto:hello@synapmarketing.com" className="text-sm text-white/40 hover:text-white transition-colors">
                hello@synapmarketing.com
              </a>
            </div>
          </div>

          <div className="border-t border-white/[0.06] mt-12 pt-8 text-center">
            <p className="text-xs text-white/30">&copy; 2026 Synap Marketing. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
